import React, { useState, useEffect } from "react";
import PageHeader from "../../../componentsII/PageHeader";
import { Card, Button } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import api from '../../../axios/api';
import { useSelector } from "react-redux";
import Loader from "../../../componentsII/Loader";
import NoData from "../../../images/NoData.jpg";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from "recharts";


const YearWiseEmissionsChart = ({ data }) => {
  // Format the data for the chart
  const formattedData = data.map(yearData => ({
    year: yearData.start_year,
    "Scope 1": parseFloat(yearData.scope_1_total) || 0,
    "Scope 2": parseFloat(yearData.scope_2_total) || 0,
    "Scope 3": parseFloat(yearData.scope_3_total) || 0,
    total: parseFloat(yearData.total_emissions) || 0
  }));

  // Color scheme matching the existing component
  const colors = {
    "Scope 1": "#FFD700", // Gold
    "Scope 2": "#9370DB", // Purple
    "Scope 3": "#FFB6C1"  // Pink
  };

  // Custom tooltip to display values with proper formatting
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md">
          <p className="font-medium text-gray-800">{`Year: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value.toFixed(2)} tCO₂e`}
            </p>
          ))}
          <p className="font-semibold mt-2">
            {`Total: ${payload.reduce((sum, entry) => sum + entry.value, 0).toFixed(2)} tCO₂e`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-80 mt-2">
    <div className="text-sm font-semibold mb-4">Year-wise Emissions Breakdown</div>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={formattedData}
        margin={{ top: 10, right: 10, left: 10, bottom: 30 }} // Increased left and bottom
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="year"
          label={{
            value: 'Year',
            position: 'insideBottom',
            // offset: -5,
            dy: 5,    
          }}
        />
        <YAxis
          label={{
            value: 'Emissions (tCO₂e)',
            angle: -90,
            position: 'insideLeft',
            dy: 40,
            dx:-10,
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend verticalAlign="top"/>
        <Bar dataKey="Scope 1" stackId="a" fill={colors["Scope 1"]} />
        <Bar dataKey="Scope 2" stackId="a" fill={colors["Scope 2"]} />
        <Bar dataKey="Scope 3" stackId="a" fill={colors["Scope 3"]} />
      </BarChart>
    </ResponsiveContainer>
  </div>  
  );
};

const FootprintAnalytics = ({ standalone = true, customYear = null, customTitle = "Footprint Analytics" }) => {
  const reduxYear = useSelector((state) => state.user.selectedYear);
  const year = customYear || reduxYear;
  const user = useSelector((state) => state.user.user);
  const [footprintData, setFootprintData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drillLevel, setDrillLevel] = useState('SCOPE');
  const [selectedScope, setSelectedScope] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasData, setHasData] = useState(true);

  const [yearWiseData, setYearWiseData] = useState([]);

  // Color map for different categories
  const colorMap = {
    "Scope 1": "#FFD700",
    "Scope 2": "#9370DB",
    "Scope 3": "#FFB6C1",
    "Fuel Consumption (Equipment)": "#4CAF50",
    "Vehicle Usage (Fuel)": "#FF5722",
    "Vehicle Usage (Distance)": "#673AB7",
    "Refrigerant Consumption": "#2196F3",
    "Industrial Gas Consumption": "#795548",
    "Natural Gas Consumption": "#009688",
    "Electricity Usage": "#3F51B5",
    "Heat and steam usage": "#E91E63",
    "Purchased Cooling": "#00BCD4",
    "Purchased Goods": "#F06292"
  };

  // Generate colors for items not in the colorMap
  const getColor = (name, index) => {
    if (colorMap[name]) return colorMap[name];

    const colors = ["#F44336", "#2196F3", "#FFEB3B", "#009688", "#E91E63", "#00BCD4",
      "#FF9800", "#795548", "#9C27B0", "#607D8B", "#8BC34A", "#CDDC39"];
    return colors[index % colors.length];
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/analytics/calculateFootprintanalytics", {
        params: {
          comp_data_year_id: year?.id
        }
      });

      if (
        response.data &&
        Object.keys(response.data).length > 0 &&
        Object.values(response.data).some(scope => scope.total && scope.total > 0)
      ) {
        setFootprintData(response.data);
        processDataForChart(response.data, 'SCOPE');
        setHasData(true);
      } else {
        setHasData(false);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please try again later.");
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearWiseData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/analytics/yearWiseEmission", {
        params: {
          companyId: user.company.id,
        }
      });

      if (response.data && response.data.length > 0) {
        setYearWiseData(response.data);
      } else {
        setHasData(false);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please try again later.");
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (year?.id) {
      fetchData();
      fetchYearWiseData();
    } else {
      setLoading(false);
      setHasData(false);
    }
  }, [year]);

  const processDataForChart = (data, level, scopeFilter = null, categoryFilter = null) => {
    let filteredData = [];
    let newTotal = 0;

    if (level === 'SCOPE') {
      // Display all scopes (Scope 1, Scope 2, Scope 3)
      filteredData = Object.keys(data)
        .filter(scopeName => scopeName !== "Scope null" && scopeName !== null)
        .map((scopeName, index) => ({
          name: scopeName,
          value: data[scopeName].total || 0,
          color: getColor(scopeName, index),
          scope: scopeName
        }));
      // Calculate total emissions across all scopes
      newTotal = Object.keys(data)
        .filter(scopeName => scopeName !== "Scope null" && scopeName !== null)
        .reduce((sum, scope) => sum + (data[scope].total || 0), 0);

      setBreadcrumbs([{ level: 'SCOPE', label: 'All Scopes' }]);


    } else if (level === 'CATEGORY' && scopeFilter !== null) {
      // Display categories within the selected scope
      const scopeData = data[scopeFilter];
      if (scopeData && scopeData.categories) {
        filteredData = Object.keys(scopeData.categories).map((categoryName, index) => ({
          name: categoryName,
          value: scopeData.categories[categoryName].total || 0,
          color: getColor(categoryName, index),
          category: categoryName,
          isScope3: scopeFilter === "Scope 3"
        }));
      }

      newTotal = scopeData ? (scopeData.total || 0) : 0;

      setBreadcrumbs([
        { level: 'SCOPE', label: 'All Scopes' },
        { level: 'CATEGORY', label: scopeFilter, scope: scopeFilter }
      ]);

    } else if (level === 'ENTITY' && categoryFilter !== null && scopeFilter !== null) {
      // Display entities within the selected category
      const scopeData = data[scopeFilter];
      const categoryData = scopeData?.categories?.[categoryFilter];

      if (categoryData && categoryData.entities) {
        filteredData = categoryData.entities.map((entity, index) => ({
          name: entity.name,
          value: entity.emission || 0,
          color: getColor(`entity-${index}`, index),
          id: entity.id,
          entity_type: entity.entity_type
        }));
      }

      newTotal = categoryData ? (categoryData.total || 0) : 0;

      // Update breadcrumbs
      setBreadcrumbs([
        { level: 'SCOPE', label: 'All Scopes' },
        { level: 'CATEGORY', label: scopeFilter, scope: scopeFilter },
        { level: 'ENTITY', label: categoryFilter, category: categoryFilter }
      ]);
    }

    // Filter out zero values for better visualization
    filteredData = filteredData.filter(item => item.value > 0);

    setChartData(filteredData);
    setTotal(newTotal.toFixed(2));
  };


  const handlePieClick = (data, index) => {
    const item = data.payload;

    if (drillLevel === 'SCOPE') {
      // Clicked on a scope, drill down to categories
      setSelectedScope(item.scope);
      setDrillLevel('CATEGORY');
      processDataForChart(footprintData, 'CATEGORY', item.scope);
    } else if (drillLevel === 'CATEGORY') {
      // For Scope 3, don't allow further drill-down
      if (selectedScope === "Scope 3") {
        return; // No further drill-down for Scope 3
      }

      // For Scope 1 and 2, drill down to entities
      setSelectedCategory(item.category);
      setDrillLevel('ENTITY');
      processDataForChart(footprintData, 'ENTITY', selectedScope, item.category);
    }
  };

  const handleBreadcrumbClick = (breadcrumb) => {
    if (breadcrumb.level === 'SCOPE') {
      setDrillLevel('SCOPE');
      setSelectedScope(null);
      setSelectedCategory(null);
      processDataForChart(footprintData, 'SCOPE');
    } else if (breadcrumb.level === 'CATEGORY') {
      setDrillLevel('CATEGORY');
      setSelectedScope(breadcrumb.scope);
      setSelectedCategory(null);
      processDataForChart(footprintData, 'CATEGORY', breadcrumb.scope);
    }
  };

  const renderBreadcrumbs = () => {
    return (
      <div className="flex items-center text-sm mb-4">
        {breadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="mx-2">/</span>}
            <span
              className={`${index < breadcrumbs.length - 1 ? 'text-blue-600 cursor-pointer hover:underline' : 'font-semibold'}`}
              onClick={() => index < breadcrumbs.length - 1 ? handleBreadcrumbClick(breadcrumb) : null}
            >
              {breadcrumb.label}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const getLevelTitle = () => {
    if (drillLevel === 'SCOPE') {
      return "Total Emissions by Scope";
    } else if (drillLevel === 'CATEGORY') {
      return `${selectedScope} Emissions by Category`;
    } else if (drillLevel === 'ENTITY') {
      return `${selectedCategory} Breakdown`;
    }
    return "Carbon Emissions";
  };

  // Custom tooltip content
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-md">
          <p className="font-medium">{data.name}</p>
          <p>{data.value.toFixed(2)} tCO₂e</p>
        </div>
      );
    }
    return null;
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      'equipment': 'Equipment',
      'vehicle': 'Vehicle',
      'location': 'Location',
      'scope_3': 'Scope 3 Category'
    };
    return labels[entityType] || entityType;
  };

  const renderDrillDownHint = () => {
    if (drillLevel === 'ENTITY') {
      return <p>Use the breadcrumb navigation above to return to higher levels.</p>;
    } else if (drillLevel === 'CATEGORY' && selectedScope === "Scope 3") {
      return <p>Scope 3 emissions are shown at category level. Use breadcrumb navigation to return to all scopes.</p>;
    } else {
      return <p>Click on a segment to drill down into more detailed breakdown.</p>;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col justify-center items-center h-64">
          <p className="text-red-500 mb-4">{error}</p>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchData}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (!hasData) {
      return (
        <div className="flex flex-col justify-center items-center h-64">
          <img src={NoData} alt="No data available" className="max-h-60 mb-0" />
          <p className="text-gray-500 mt-0">No emissions data available for the selected year.</p>
        </div>
      );
    }

    const isCursorPointer = (item) => {
      if (drillLevel === 'ENTITY') return false;
      if (drillLevel === 'CATEGORY' && selectedScope === "Scope 3") return false;
      return true;
    };

    return (
      <>
        {renderBreadcrumbs()}

        <div className="flex flex-col md:flex-row items-start gap-8">
          <div className="w-64 h-64">
            <PieChart width={256} height={256}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={100}
                paddingAngle={chartData.length > 1 ? 2 : 0}
                dataKey="value"
                onClick={handlePieClick}
                cursor="pointer"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{ cursor: isCursorPointer(entry) ? 'pointer' : 'default' }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </div>

          <div className="flex-1">
            <h2 className="text-xl font-semibold mb-2">{getLevelTitle()}</h2>
            <p className="text-4xl font-bold mb-6">
              {total}
              <span className="text-sm text-gray-500 font-semibold"> tCO₂e</span>
            </p>

            <div className="space-y-2">
              {chartData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="font-medium">{item.name}</span>
                  {drillLevel === 'ENTITY' && item.entity_type && (
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {getEntityTypeLabel(item.entity_type)}
                    </span>
                  )}
                  <span className="ml-2">{item.value.toFixed(2)}</span>
                  <span className="text-gray-500 text-sm">tCO₂e</span>
                  {item.value > 0 && total > 0 && (
                    <span className="text-gray-500 text-sm">
                      ({((item.value / parseFloat(total)) * 100).toFixed(1)}%)
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-500">
          {drillLevel === 'ENTITY' ? (
            <p>Use the breadcrumb navigation above to return to higher levels.</p>
          ) : (
            <p>Click on a segment to drill down into more detailed breakdown.</p>
          )}
        </div>
      </>
    );
  };

  if (standalone) {
    return (
      <div>
        <PageHeader
          title={customTitle}
          text={[
            "View and analyze your total calculated footprint through different metrics and visualizations.",
            "Analytics can take up to 5 minutes to refresh."
          ]}
        >
          <div className="flex flex-col md:flex-row items-start gap-8">
            <Card className="w-[50%] p-6">
              {renderContent()}
            </Card>
            <Card className="w-[50%] p-6">
              {yearWiseData.length > 0 && (
                <YearWiseEmissionsChart data={yearWiseData} />
              )}

            </Card>
          </div>
        </PageHeader>
      </div>
    );
  }

  return (
    <Card className="w-full p-6">
      {renderContent()}
    </Card>
  );
};

export default FootprintAnalytics;